import { useState, useEffect, useCallback } from 'react'

/**
 * Concierge — admin call queue for the warm-call GTM motion.
 *
 * See docs/VENUE_STRATEGY.md §"Go-to-Market: The Concierge Call."
 * Each row is a shadow venue with a recent consumer report. Click
 * a row to load the report alongside (right panel on desktop, below
 * on mobile). Log the outcome of the call inline.
 *
 * Admin-gated via the existing LoginGate; all API routes hit
 * /api/admin/concierge/* and require admin cookie auth.
 */

interface QueueItem {
  venue: {
    id: string
    slug: string
    name: string
    type: string | null
    city: string | null
    state: string | null
    country: string | null
    address: string | null
    contactPhone: string | null
    googlePlaceId: string | null
    reportCount: string | null
    lastReportAt: string | null
    signedUp: boolean
    source: string | null
  }
  recent_item: {
    id: string
    referenceCode: string | null
    category: string | null
    subcategory: string | null
    brand: string | null
    colorPrimary: string | null
    venueName: string | null
    dateOccurred: string | null
    timeApproximate: string | null
    distinguishingMarks: string | null
    notes: string | null
    narrative: string | null
    voiceTranscript: string | null
    reporterName: string | null
    reporterPhone: string | null
    reporterEmail: string | null
    createdAt: string
  } | null
  last_outreach: {
    outcome: string
    stepReached: number
    calledAt: string | null
    createdAt: string
    notes: string | null
  } | null
}

const OUTCOME_OPTIONS = [
  { value: 'no_answer', label: 'No answer' },
  { value: 'voicemail', label: 'Left voicemail' },
  { value: 'spoke', label: 'Spoke (no outcome)' },
  { value: 'interested', label: 'Interested' },
  { value: 'converted', label: 'Converted (logged with us)' },
  { value: 'rejected', label: 'Rejected / opt-out' },
] as const

function relativeTime(iso: string | null): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (isNaN(ms)) return ''
  const m = Math.round(ms / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d === 1) return 'yesterday'
  if (d < 14) return `${d} days ago`
  const w = Math.round(d / 7)
  return `${w}w ago`
}

function pretty(s: string | null | undefined): string {
  if (!s) return ''
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function Concierge() {
  const [queue, setQueue] = useState<QueueItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null) // venue.id
  const [stats, setStats] = useState<Array<{ outcome: string; count: number }>>([])
  const [logState, setLogState] = useState<{
    venueId: string | null
    outcome: string
    notes: string
    stepReached: number
    saving: boolean
  }>({ venueId: null, outcome: 'no_answer', notes: '', stepReached: 0, saving: false })

  const loadQueue = useCallback(async () => {
    try {
      setLoading(true)
      const [q, s] = await Promise.all([
        fetch('/api/admin/concierge/queue', { credentials: 'include' }),
        fetch('/api/admin/concierge/stats', { credentials: 'include' }),
      ])
      if (!q.ok) throw new Error(`Queue fetch failed: ${q.status}`)
      const qData = await q.json()
      setQueue(qData.items ?? [])
      if (s.ok) {
        const sData = await s.json()
        setStats(sData.last_30_days ?? [])
      }
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queue')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  const selectedItem = queue.find((q) => q.venue.id === selected) ?? null

  const handleOpenLog = (venueId: string) => {
    setLogState({
      venueId,
      outcome: 'no_answer',
      notes: '',
      stepReached: 0,
      saving: false,
    })
  }

  const handleLogSave = async () => {
    if (!logState.venueId || logState.saving) return
    setLogState((s) => ({ ...s, saving: true }))
    try {
      const item = queue.find((q) => q.venue.id === logState.venueId)
      const res = await fetch('/api/admin/concierge/log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venue_id: logState.venueId,
          linked_item_id: item?.recent_item?.id ?? null,
          outcome: logState.outcome,
          step_reached: logState.stepReached,
          notes: logState.notes || null,
        }),
      })
      if (!res.ok) throw new Error(`Log failed: ${res.status}`)
      setLogState({
        venueId: null,
        outcome: 'no_answer',
        notes: '',
        stepReached: 0,
        saving: false,
      })
      await loadQueue()
    } catch (err) {
      console.error(err)
      setLogState((s) => ({ ...s, saving: false }))
    }
  }

  const handleSignup = async (venueId: string) => {
    if (!confirm('Mark this venue as SIGNED UP?')) return
    try {
      const res = await fetch(`/api/admin/concierge/signup/${venueId}`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`Signup failed: ${res.status}`)
      await loadQueue()
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col px-5 py-6 overflow-y-auto">
      <header className="shrink-0 flex flex-col gap-3 max-w-5xl mx-auto w-full">
        <div className="flex items-center gap-3">
          <div style={{ width: 24, height: 2, background: 'var(--gold)' }} />
          <span
            className="text-[10px] uppercase"
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              letterSpacing: '0.22em',
              color: 'var(--ink-3)',
            }}
          >
            Concierge · Daily Outreach
          </span>
        </div>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontWeight: 500,
            fontSize: 'clamp(28px, 4.5vw, 36px)',
            lineHeight: 1.05,
            letterSpacing: '-0.015em',
            color: 'var(--ink-1)',
            margin: 0,
          }}
        >
          {loading ? 'Loading queue…' : `${queue.length} shadow ${queue.length === 1 ? 'venue' : 'venues'} with a recent report.`}
        </h1>
        {stats.length > 0 && (
          <div className="flex flex-wrap gap-3 text-[12px]" style={{ color: 'var(--ink-3)' }}>
            <span style={{ fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Last 30 days:
            </span>
            {stats.map((s) => (
              <span key={s.outcome}>
                <span style={{ color: 'var(--ink-1)', fontWeight: 600 }}>{s.count}</span> {pretty(s.outcome)}
              </span>
            ))}
          </div>
        )}
      </header>

      {error && (
        <div className="mt-6 max-w-5xl mx-auto w-full p-4 border" style={{ borderColor: 'var(--error)', color: 'var(--error)' }}>
          {error}
        </div>
      )}

      {/* Two-pane on desktop, stack on mobile */}
      <div className="mt-8 flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto w-full">
        {/* Queue list */}
        <section className="overflow-y-auto pr-2">
          <h2
            className="text-[11px] uppercase mb-3"
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              letterSpacing: '0.22em',
              color: 'var(--accent)',
            }}
          >
            Queue
          </h2>
          <ol className="flex flex-col" style={{ paddingLeft: 0 }}>
            {queue.length === 0 && !loading && (
              <p style={{ color: 'var(--ink-3)' }}>Nothing in the queue. Either everyone's been called, or no new shadow-venue reports landed in the last 30 days.</p>
            )}
            {queue.map((item) => {
              const isSelected = selected === item.venue.id
              return (
                <li
                  key={item.venue.id}
                  className={`border-t py-4 cursor-pointer transition-colors ${isSelected ? 'pl-3' : ''}`}
                  style={{
                    borderColor: 'var(--border-subtle)',
                    background: isSelected ? 'var(--surface-2)' : 'transparent',
                    borderLeft: isSelected ? '2px solid var(--accent)' : '2px solid transparent',
                  }}
                  onClick={() => setSelected(item.venue.id)}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3
                      style={{
                        fontFamily: 'var(--font-serif)',
                        fontSize: '17px',
                        color: 'var(--ink-1)',
                        margin: 0,
                      }}
                    >
                      {item.venue.name}
                    </h3>
                    <span className="text-[11px] shrink-0" style={{ color: 'var(--ink-3)' }}>
                      {item.venue.reportCount} {item.venue.reportCount === '1' ? 'report' : 'reports'}
                    </span>
                  </div>
                  <p className="mt-1 text-[13px]" style={{ color: 'var(--ink-2)' }}>
                    {[item.venue.city, item.venue.state].filter(Boolean).join(', ')}
                    {item.venue.contactPhone && ` · ${item.venue.contactPhone}`}
                  </p>
                  <p className="mt-1 text-[12px]" style={{ color: 'var(--ink-3)' }}>
                    Last report {relativeTime(item.venue.lastReportAt)}
                    {item.last_outreach && (
                      <>
                        {' · '}
                        <span style={{ color: 'var(--ink-2)' }}>
                          last call {relativeTime(item.last_outreach.calledAt)} ({pretty(item.last_outreach.outcome)})
                        </span>
                      </>
                    )}
                  </p>
                </li>
              )
            })}
          </ol>
        </section>

        {/* Detail / log panel */}
        <section className="overflow-y-auto pl-2 border-l" style={{ borderColor: 'var(--border-subtle)' }}>
          <h2
            className="text-[11px] uppercase mb-3"
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              letterSpacing: '0.22em',
              color: 'var(--accent)',
            }}
          >
            {selectedItem ? 'Report on file' : 'Select a venue'}
          </h2>

          {!selectedItem && (
            <p className="text-[14px]" style={{ color: 'var(--ink-3)' }}>
              Click a venue in the queue to see the linked report and log the outreach.
            </p>
          )}

          {selectedItem && (
            <div className="flex flex-col gap-5">
              <div>
                <h3
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: '24px',
                    color: 'var(--ink-1)',
                    margin: 0,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {selectedItem.venue.name}
                </h3>
                <p className="mt-1 text-[14px]" style={{ color: 'var(--ink-2)' }}>
                  {selectedItem.venue.address ?? [selectedItem.venue.city, selectedItem.venue.state].filter(Boolean).join(', ')}
                </p>
                {selectedItem.venue.contactPhone && (
                  <p className="mt-1 text-[14px]">
                    <a
                      href={`tel:${selectedItem.venue.contactPhone}`}
                      style={{ color: 'var(--accent)', fontWeight: 500 }}
                    >
                      📞 {selectedItem.venue.contactPhone}
                    </a>
                  </p>
                )}
                {selectedItem.venue.googlePlaceId && (
                  <p className="mt-1 text-[12px]">
                    <a
                      href={`https://www.google.com/maps/place/?q=place_id:${selectedItem.venue.googlePlaceId}`}
                      target="_blank"
                      rel="noopener"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      Google Maps →
                    </a>
                  </p>
                )}
              </div>

              {selectedItem.recent_item && (
                <div className="border-t pt-5" style={{ borderColor: 'var(--border-subtle)' }}>
                  <h4
                    className="text-[10px] uppercase mb-2"
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 600,
                      letterSpacing: '0.22em',
                      color: 'var(--ink-3)',
                    }}
                  >
                    Most recent guest report ({selectedItem.recent_item.referenceCode})
                  </h4>
                  <p className="text-[14px]" style={{ color: 'var(--ink-1)', lineHeight: 1.5 }}>
                    {selectedItem.recent_item.narrative ??
                      selectedItem.recent_item.voiceTranscript?.split('\n').filter(Boolean).join(' ') ??
                      'No transcript on file.'}
                  </p>
                  <dl className="mt-3 grid grid-cols-2 gap-y-1 text-[12px]" style={{ color: 'var(--ink-2)' }}>
                    {selectedItem.recent_item.brand && (
                      <>
                        <dt style={{ color: 'var(--ink-3)' }}>Brand</dt>
                        <dd>{selectedItem.recent_item.brand}</dd>
                      </>
                    )}
                    {selectedItem.recent_item.colorPrimary && (
                      <>
                        <dt style={{ color: 'var(--ink-3)' }}>Color</dt>
                        <dd>{selectedItem.recent_item.colorPrimary}</dd>
                      </>
                    )}
                    {selectedItem.recent_item.subcategory && (
                      <>
                        <dt style={{ color: 'var(--ink-3)' }}>Type</dt>
                        <dd>{pretty(selectedItem.recent_item.subcategory)}</dd>
                      </>
                    )}
                    {selectedItem.recent_item.dateOccurred && (
                      <>
                        <dt style={{ color: 'var(--ink-3)' }}>Date</dt>
                        <dd>
                          {selectedItem.recent_item.dateOccurred}
                          {selectedItem.recent_item.timeApproximate && ` (${selectedItem.recent_item.timeApproximate})`}
                        </dd>
                      </>
                    )}
                    {selectedItem.recent_item.distinguishingMarks && (
                      <>
                        <dt style={{ color: 'var(--ink-3)' }}>Marks</dt>
                        <dd>{selectedItem.recent_item.distinguishingMarks}</dd>
                      </>
                    )}
                    {(selectedItem.recent_item.reporterName ||
                      selectedItem.recent_item.reporterEmail ||
                      selectedItem.recent_item.reporterPhone) && (
                      <>
                        <dt style={{ color: 'var(--ink-3)' }}>Reporter</dt>
                        <dd>
                          {[
                            selectedItem.recent_item.reporterName,
                            selectedItem.recent_item.reporterEmail,
                            selectedItem.recent_item.reporterPhone,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              )}

              <div className="border-t pt-5 flex flex-col gap-3" style={{ borderColor: 'var(--border-subtle)' }}>
                <h4
                  className="text-[10px] uppercase"
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 600,
                    letterSpacing: '0.22em',
                    color: 'var(--ink-3)',
                  }}
                >
                  Log this call
                </h4>

                {logState.venueId !== selectedItem.venue.id ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenLog(selectedItem.venue.id)}
                      className="px-4 py-2 text-[12px] uppercase"
                      style={{
                        background: 'var(--accent)',
                        color: 'var(--text-on-accent)',
                        fontWeight: 600,
                        letterSpacing: '0.18em',
                        border: 0,
                        borderRadius: 0,
                      }}
                    >
                      Log Outcome
                    </button>
                    {!selectedItem.venue.signedUp && (
                      <button
                        type="button"
                        onClick={() => handleSignup(selectedItem.venue.id)}
                        className="px-4 py-2 text-[12px] uppercase"
                        style={{
                          background: 'transparent',
                          color: 'var(--ink-1)',
                          border: '1px solid var(--border-default)',
                          fontWeight: 600,
                          letterSpacing: '0.18em',
                          borderRadius: 0,
                        }}
                      >
                        Mark Signed Up
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <select
                      value={logState.outcome}
                      onChange={(e) => setLogState((s) => ({ ...s, outcome: e.target.value }))}
                      className="px-3 py-2 text-[14px]"
                      style={{
                        border: '1px solid var(--border-default)',
                        borderRadius: 0,
                        background: 'var(--surface-1)',
                        color: 'var(--ink-1)',
                      }}
                    >
                      {OUTCOME_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <label className="flex flex-col gap-1 text-[12px]" style={{ color: 'var(--ink-3)' }}>
                      Step reached (0–5)
                      <input
                        type="number"
                        min={0}
                        max={5}
                        value={logState.stepReached}
                        onChange={(e) => setLogState((s) => ({ ...s, stepReached: parseInt(e.target.value) || 0 }))}
                        className="px-3 py-2 text-[14px]"
                        style={{
                          border: '1px solid var(--border-default)',
                          borderRadius: 0,
                          background: 'var(--surface-1)',
                          color: 'var(--ink-1)',
                        }}
                      />
                    </label>
                    <textarea
                      value={logState.notes}
                      onChange={(e) => setLogState((s) => ({ ...s, notes: e.target.value }))}
                      rows={3}
                      placeholder="Notes (optional)"
                      className="px-3 py-2 text-[14px] resize-none"
                      style={{
                        border: '1px solid var(--border-default)',
                        borderRadius: 0,
                        background: 'var(--surface-1)',
                        color: 'var(--ink-1)',
                        fontFamily: 'inherit',
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleLogSave}
                        disabled={logState.saving}
                        className="px-4 py-2 text-[12px] uppercase"
                        style={{
                          background: 'var(--accent)',
                          color: 'var(--text-on-accent)',
                          fontWeight: 600,
                          letterSpacing: '0.18em',
                          border: 0,
                          borderRadius: 0,
                          opacity: logState.saving ? 0.5 : 1,
                        }}
                      >
                        {logState.saving ? 'Saving…' : 'Save Log'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogState((s) => ({ ...s, venueId: null }))}
                        className="px-4 py-2 text-[12px]"
                        style={{
                          background: 'transparent',
                          color: 'var(--ink-2)',
                          border: '1px solid var(--border-default)',
                          borderRadius: 0,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
